import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Layout from './components/Layout.jsx';
import RequiresSingleEntity from './components/RequiresSingleEntity.jsx';
import LockScreen from './components/LockScreen.jsx';
import { useAppLock } from './hooks/useAppLock.js';
import queryClient from './lib/queryClient.js';

// Auth pages (public)
import LoginPage from './pages/LoginPage.jsx';
import TwoFactorSetupPage from './pages/TwoFactorSetupPage.jsx';
import TwoFactorVerifyPage from './pages/TwoFactorVerifyPage.jsx';

// Protected pages
import HomePage from './pages/HomePage.jsx';
import ExpensesLayout from './pages/ExpensesLayout.jsx';
import TransactionsTabPage from './pages/TransactionsTabPage.jsx';
import CategoriesRulesTabPage from './pages/CategoriesRulesTabPage.jsx';
import CashFlowPage from './pages/CashFlowPage.jsx';
import DebtsPage from './pages/DebtsPage.jsx';
import DebtDetailPage from './pages/DebtDetailPage.jsx';
import ForecastsPage from './pages/ForecastsPage.jsx';
import UpcomingPage from './pages/UpcomingPage.jsx';
import ClientInvoicesPage from './pages/ClientInvoicesPage.jsx';
import EmployeesPage from './pages/EmployeesPage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';
import VatDeductiblePage from './pages/VatDeductiblePage.jsx';
import EntitiesAdminPage from './pages/EntitiesAdminPage.jsx';
import IntercompanyPage from './pages/IntercompanyPage.jsx';
import AssociatesPage from './pages/AssociatesPage.jsx';
import AssociateAccountDetailPage from './pages/AssociateAccountDetailPage.jsx';
import AssociateTrancheDetailPage from './pages/AssociateTrancheDetailPage.jsx';
import SalesAnalysisPage from './pages/SalesAnalysisPage.jsx';
import SalesLayout from './pages/SalesLayout.jsx';
import ShopifyOrdersListPage from './pages/ShopifyOrdersListPage.jsx';
import ShopifyOrderDetailPage from './pages/ShopifyOrderDetailPage.jsx';
import ProductsPage from './pages/ProductsPage.jsx';
import ProductDetailPage from './pages/ProductDetailPage.jsx';
import LotsAnalysisPage from './pages/LotsAnalysisPage.jsx';
import LotDetailPage from './pages/LotDetailPage.jsx';
import PettyCashPage from './pages/PettyCashPage.jsx';
import ReceiptImportPage from './pages/ReceiptImportPage.jsx';

function ProtectedRoute({ children }) {
  const navigate = useNavigate();
  const lock = useAppLock();

  useEffect(() => {
    const handler = () => {
      queryClient.clear();
      navigate('/login', { replace: true });
    };
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, [navigate]);

  // Démarrage à froid verrouillé : ni les pages ni leurs requêtes ne sont montées.
  if (lock.cold) {
    return <LockScreen failed={lock.failed} busy={lock.busy} onUnlock={lock.unlock} />;
  }

  return (
    <>
      {children}
      {lock.locked && <LockScreen failed={lock.failed} busy={lock.busy} onUnlock={lock.unlock} />}
      {!lock.locked && lock.covered && <LockScreen showActions={false} />}
    </>
  );
}

export default function App() {
  const [preauthToken, setPreauthToken] = useState(null);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage onPreauthToken={setPreauthToken} />} />
      <Route path="/2fa/setup" element={<TwoFactorSetupPage preauthToken={preauthToken} />} />
      <Route path="/2fa/verify" element={<TwoFactorVerifyPage preauthToken={preauthToken} />} />

      {/* Protected routes — wrapped in Layout */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<HomePage />} />

                {/* Dépenses — shell with Transactions / Catégories & règles */}
                <Route
                  path="/depenses"
                  element={
                    <RequiresSingleEntity label="Les dépenses se gèrent par société">
                      <ExpensesLayout />
                    </RequiresSingleEntity>
                  }
                >
                  <Route index element={<Navigate to="transactions" replace />} />
                  <Route path="transactions" element={<TransactionsTabPage />} />
                  <Route path="recurrences" element={<Navigate to="/previsionnels" replace />} />
                  <Route path="futures" element={<Navigate to="/previsionnels" replace />} />
                  <Route path="categories" element={<CategoriesRulesTabPage />} />
                </Route>

                {/* Legacy redirections */}
                <Route path="/expenses" element={<Navigate to="/depenses" replace />} />
                <Route path="/expenses/*" element={<Navigate to="/depenses" replace />} />
                <Route path="/transactions" element={<Navigate to="/depenses/transactions" replace />} />
                <Route
                  path="/transactions/unclassified"
                  element={<Navigate to="/depenses/transactions?unclassified=true" replace />}
                />
                <Route path="/recurrences" element={<Navigate to="/previsionnels" replace />} />
                <Route path="/future-expenses" element={<Navigate to="/previsionnels" replace />} />
                <Route
                  path="/settings/categories"
                  element={<Navigate to="/depenses/categories" replace />}
                />
                <Route
                  path="/settings/mapping-rules"
                  element={<Navigate to="/depenses/categories" replace />}
                />

                {/* Pages compatibles vue groupe (Accueil + ces 3 pages agrègent les entités) */}
                <Route path="/cash-flow" element={<CashFlowPage />} />
                <Route path="/a-payer" element={<UpcomingPage />} />

                {/* Pages qui requièrent une société spécifique */}
                <Route
                  path="/factures"
                  element={
                    <RequiresSingleEntity label="Les factures se gèrent par société">
                      <ClientInvoicesPage />
                    </RequiresSingleEntity>
                  }
                />
                <Route
                  path="/previsionnels"
                  element={
                    <RequiresSingleEntity label="Les prévisionnels se gèrent par société">
                      <ForecastsPage />
                    </RequiresSingleEntity>
                  }
                />
                <Route
                  path="/employes"
                  element={
                    <RequiresSingleEntity label="Les employés se gèrent par société">
                      <EmployeesPage />
                    </RequiresSingleEntity>
                  }
                />
                <Route
                  path="/dettes"
                  element={
                    <RequiresSingleEntity label="Les dettes se gèrent par société">
                      <DebtsPage />
                    </RequiresSingleEntity>
                  }
                />
                <Route
                  path="/dettes/:id"
                  element={
                    <RequiresSingleEntity label="Les dettes se gèrent par société">
                      <DebtDetailPage />
                    </RequiresSingleEntity>
                  }
                />
                <Route
                  path="/tva"
                  element={
                    <RequiresSingleEntity label="La TVA se gère par société">
                      <VatDeductiblePage />
                    </RequiresSingleEntity>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <RequiresSingleEntity label="Les paramètres se gèrent par société">
                      <SettingsPage />
                    </RequiresSingleEntity>
                  }
                />
                <Route path="/admin/entities" element={<EntitiesAdminPage />} />
                <Route path="/refacturation" element={<IntercompanyPage />} />
                <Route path="/justificatifs" element={<ReceiptImportPage />} />

                {/* Ventes Shopify — layout avec onglets */}
                <Route path="/ventes" element={<SalesLayout />}>
                  <Route index element={<Navigate to="analyse" replace />} />
                  <Route path="analyse" element={<SalesAnalysisPage />} />
                  <Route path="lots" element={<LotsAnalysisPage />} />
                  <Route path="lots/:lot_number" element={<LotDetailPage />} />
                  <Route path="commandes" element={<ShopifyOrdersListPage />} />
                  <Route path="commandes/:id" element={<ShopifyOrderDetailPage />} />
                  <Route path="produits" element={<ProductsPage />} />
                  <Route path="produits/:sku" element={<ProductDetailPage />} />
                </Route>

                {/* Caisse (petty cash) — mono-entité */}
                <Route
                  path="/caisse"
                  element={
                    <RequiresSingleEntity label="La caisse se gère par société">
                      <PettyCashPage />
                    </RequiresSingleEntity>
                  }
                />

                {/* Associés (CCA) — page liste groupe-compatible, détails mono-entité */}
                <Route path="/associes" element={<AssociatesPage />} />
                <Route
                  path="/associes/comptes/:id"
                  element={
                    <RequiresSingleEntity label="Les comptes associés se gèrent par société">
                      <AssociateAccountDetailPage />
                    </RequiresSingleEntity>
                  }
                />
                <Route
                  path="/associes/comptes/:id/tranches/:trancheId"
                  element={
                    <RequiresSingleEntity label="Les comptes associés se gèrent par société">
                      <AssociateTrancheDetailPage />
                    </RequiresSingleEntity>
                  }
                />

                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
